import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type LayoutChangeEvent,
  type NativeSyntheticEvent,
  type TextInputKeyPressEventData,
} from "react-native";
import { BAND_GLYPH, NO_ROUND, type IntensityBand } from "@heraion/shared";

import type { GridGestureBundle } from "../IntensityGrid/useGridGestures";
import { Icon } from "../Icon/Icon";
import { CellMarks } from "../IntensityGrid/CellMarks";
import { TEXT_FIELD_BOX_H, TextField } from "../primitives/TextField";
import type { UiTheme } from "../theme";
import { stateAria } from "../a11y";

/** The least height of a body row (the 44pt tap floor). */
export const ROW_MIN = 44;
/** The borders a row's box adds to its content: the hairline, and the cell's ring. */
export const LINE = 1;
export const RING = 2;

/** One cell as the grid reads it: its lens text (null: not in that round),
 *  its band, whether it is by hand, open, in the selected block (or the
 *  block a fill would cover), and what a screen reader says. */
export type RoundCell = {
  text: string | null;
  band: IntensityBand | null;
  byHand: boolean;
  open: boolean;
  inRange: boolean;
  spoken: string;
};

/** The open cell's Sets field (2b): its value, what it opens with when a key
 *  opened it, and its keys. `onCommit` gets the text when it changed. */
export type CellFieldSpec = {
  value: string;
  seed?: string;
  a11yLabel: string;
  onCommit: (text: string) => void;
  onClose: () => void;
  onTab: (back: boolean) => void;
};

// One cell of the wide day grid (#336 slice 6). The band paints a box inside
// the cell's ring, so the column shows between two cells of the same band
// and each round reads apart (brief §15 row 28). The glyph and the «by hand»
// mark have a line of their own above the value: apart at any font size. The
// open cell's ring is ink, with a surface edge inside it: one of the two
// reads at 3:1 on every fill. The rest of a selected block has a dashed ring:
// a shape, not a colour. One shell; the open cell's field (2b) only swaps the
// value for an input, in a plain View, so no button wraps an input. The fill
// handle sits on the block's last cell, for the mouse (the web only).
export function RoundGridCell({
  theme,
  id,
  cell,
  height,
  onLayout,
  onPress,
  gesture,
  field,
  fillHandle,
  readOnly = false,
}: {
  theme: UiTheme;
  /** The cell's id on the web page (gridCellId). */
  id: string;
  cell: RoundCell;
  height: number;
  /** The content's height, for the row to take the tallest. */
  onLayout: (e: LayoutChangeEvent) => void;
  onPress: () => void;
  /** The hold that starts a selection, and the screen reader's named actions. */
  gesture?: GridGestureBundle;
  /** The open cell's field, while it is open. */
  field?: CellFieldSpec;
  /** The fill handle's id, on the block's last cell only. */
  fillHandle?: string;
  /** Nothing to edit (#358): a ring marks a cell by hand, not the pencil. */
  readOnly?: boolean;
}) {
  const s = useMemo(() => makeStyles(theme), [theme]);
  const p = theme.palette;
  const scale = cell.band ? theme.intensity[cell.band] : null;
  const ink = cell.text === null ? p.ink3 : cell.byHand ? (scale?.on ?? p.warn) : (scale?.on ?? p.ink);
  const ring = [cell.inRange && !cell.open && s.range, cell.open && s.open];
  const child = field ? (
    <CellField theme={theme} spec={field} />
  ) : (
    <Text style={[s.value, { color: ink }, cell.byHand && s.hand]}>{cell.text ?? NO_ROUND}</Text>
  );
  const body = (pressed: boolean): ReactNode => (
    <>
      {scale ? (
        <View
          pointerEvents="none"
          style={[s.fill, { backgroundColor: scale.fill }, cell.open && s.fillOpen, pressed && s.dim]}
        />
      ) : null}
      <View onLayout={onLayout} style={s.content}>
        {cell.band || cell.byHand ? (
          <CellMarks
            theme={theme}
            glyph={cell.band ? BAND_GLYPH[cell.band] : ""}
            ink={ink}
            right={cell.byHand ? <Icon name={readOnly ? "circle" : "edit"} size={theme.typography.sizes.sm} color={ink} /> : null}
          />
        ) : null}
        {child}
      </View>
      {fillHandle ? (
        <View nativeID={fillHandle} style={s.handleHit} importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
          <View style={[s.handle, { backgroundColor: p.ink, borderColor: p.surface }]} />
        </View>
      ) : null}
    </>
  );
  return (
    <View style={[s.row, { height }]}>
      {field ? (
        <View nativeID={id} style={[s.cell, ...ring]}>
          {body(false)}
        </View>
      ) : (
        <Pressable
          nativeID={id}
          accessibilityRole="button"
          accessibilityLabel={cell.spoken}
          accessibilityState={{ selected: cell.open || cell.inRange }}
          onPress={onPress}
          {...gesture}
          // RNW drops accessibilityState.selected: «pressed» says «in the block»
          // on the web (the role stays a button, so aria-selected has no home),
          // and Shift+F10 is the key to the range actions.
          {...stateAria({ selected: cell.open || cell.inRange }, "button")}
          aria-keyshortcuts="Shift+F10"
          style={({ pressed }) => [s.cell, pressed && !scale && s.pressed, ...ring]}
        >
          {({ pressed }) => body(pressed)}
        </Pressable>
      )}
    </View>
  );
}

// The open cell's field, focused as it opens. Enter or leaving it writes what
// was typed, Tab too and moves on, Esc leaves it as it was; a text the same
// as the value writes nothing. Once only: the field closes as it writes.
function CellField({ theme, spec }: { theme: UiTheme; spec: CellFieldSpec }) {
  const { fontScale } = useWindowDimensions();
  const s = useMemo(() => makeFieldStyles(theme, fontScale), [theme, fontScale]);
  const [text, setText] = useState(spec.seed ?? spec.value);
  const done = useRef(false);
  const finish = (write: boolean) => {
    if (done.current) return;
    done.current = true;
    if (write && text.trim() !== spec.value.trim()) spec.onCommit(text);
    spec.onClose();
  };
  const onKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData & { shiftKey?: boolean }>) => {
    const key = e.nativeEvent.key;
    if (key === "Escape") finish(false);
    if (key !== "Tab") return;
    // RNW hands the DOM event: the browser must not move the focus as well.
    (e as unknown as { preventDefault?: () => void }).preventDefault?.();
    finish(true);
    spec.onTab(e.nativeEvent.shiftKey === true);
  };
  return (
    <TextField
      theme={theme}
      numeric
      keyboardType="number-pad"
      maxLength={SET_CHARS}
      autoFocus
      selectTextOnFocus={spec.seed === undefined}
      value={text}
      onChangeText={setText}
      onSubmitEditing={() => finish(true)}
      onBlur={() => finish(true)}
      onKeyPress={onKeyPress}
      returnKeyType="done"
      accessibilityLabel={spec.a11yLabel}
      hitSlop={FIELD_SLOP}
      style={s.field}
    />
  );
}

// The compact box (the TextField default), up to the 44 floor by hitSlop on
// native; on web the 44pt row around it is the floor.
const SLOP = (ROW_MIN - TEXT_FIELD_BOX_H) / 2;
const FIELD_SLOP = { top: SLOP, bottom: SLOP, left: SLOP, right: SLOP };
const BORDER = 1;
/** The set count is at most two digits. */
const SET_CHARS = 2;

function makeFieldStyles(theme: UiTheme, fontScale: number) {
  const { spacing: sp, typography: ty } = theme;
  const pad = sp.s2;
  // The ceiling is the content of the narrowest round column (72 less ring and
  // padding), which «20» fits at a font of 1.5; the floor is the square box.
  // A longer value scrolls inside it.
  const ceiling = sp.s8 + sp.s5;
  // `SET_CHARS` mono characters at the text size now in force, plus padding and
  // border: it grows with the font.
  const fit = Math.ceil(SET_CHARS * ty.monoAdvanceEm * ty.sizes.base * fontScale) + 2 * pad + 2 * BORDER;
  return StyleSheet.create({
    field: {
      width: Math.min(Math.max(fit, TEXT_FIELD_BOX_H), ceiling),
      paddingHorizontal: pad,
      fontFamily: ty.fonts.mono,
      alignSelf: "center",
    },
  });
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // The row's separator is this box's, so the cell's ring keeps four sides.
    row: { minHeight: ROW_MIN, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: p.border },
    cell: {
      flex: 1,
      justifyContent: "center",
      paddingHorizontal: s.s1,
      // The open cell's ring takes no room: every cell has it, unseen.
      borderWidth: RING,
      borderColor: "transparent",
    },
    // Inside the ring: the transparent ring is the gap between two fills.
    fill: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, borderRadius: r.r2 },
    fillOpen: { borderWidth: RING, borderColor: p.surface, borderRadius: 0 },
    dim: { opacity: 0.8 },
    content: { paddingVertical: s.s1 },
    pressed: { backgroundColor: p.surface2 },
    open: { borderColor: p.ink },
    range: { borderColor: p.ink, borderStyle: "dashed" },
    // The fill handle: a square on the cell's corner, over the ring, in a
    // box inside the cell for the mouse to find.
    handleHit: {
      position: "absolute",
      right: -RING,
      bottom: -RING,
      width: s.s6,
      height: s.s6,
      alignItems: "flex-end",
      justifyContent: "flex-end",
    },
    handle: { width: s.s3, height: s.s3, borderWidth: 1 },
    value: { fontSize: ty.sizes.sm, fontFamily: ty.fonts.mono, textAlign: "center" },
    hand: { fontWeight: "700" },
  });
}
